import { useSyncExternalStore } from 'react'
import { apiUrl } from '@/lib/api-client'

/**
 * How this product signs people in, read from the API at run time (`GET /mocktest/auth/config`, spec 5.1) so a rollback is
 * only an API environment flip and never a front-end release.
 *
 *  - `legacy`: today's password and Google sign-in, unchanged.
 *  - `both`:   Edvantage ID first, with "Use your old password instead" as the way back (the default rollback).
 *  - `oidc`:   Edvantage ID only. The old credential routes answer 410.
 *
 * The answer is held in one small external store, not in React state, so every page can read it (`useAuthConfig`) without a
 * provider and the auth context can start the one request. Until somebody asks, the status is `idle` and the mode reads as
 * `legacy`: that is what keeps every page that never loaded a config behaving exactly as it did before this change.
 */

export type AuthMode = 'legacy' | 'both' | 'oidc'

export interface AuthConfig {
  mode: AuthMode
  /** Relative, on this origin: `/mocktest/auth/oidc/start`. */
  start_url: string
  /** The same start route with `intent=student&prompt=create`; null where the product has no self sign-up. */
  signup_url: string | null
  /** Where a person manages their own account and security: `https://id.edvantage.me/account`. */
  account_url: string
  /** Where staff and teachers manage accounts: `https://id.edvantage.me/portal`. */
  manage_url: string
}

export type AuthConfigStatus = 'idle' | 'loading' | 'ready' | 'failed'

interface Snapshot { status: AuthConfigStatus; config: AuthConfig | null }

const IDLE: Snapshot = { status: 'idle', config: null }
let snapshot: Snapshot = IDLE
let pending: Promise<AuthConfig | null> | null = null
const listeners = new Set<() => void>()

function publish(next: Snapshot) {
  snapshot = next
  listeners.forEach((notify) => notify())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** What an API that predates the config route means: the product still signs in the old way. */
const LEGACY_CONFIG: AuthConfig = {
  mode: 'legacy',
  start_url: '/mocktest/auth/oidc/start',
  signup_url: null,
  account_url: 'https://id.edvantage.me/account',
  manage_url: 'https://id.edvantage.me/portal',
}

/** A path on this origin: one slash, then not a second slash or a backslash (`//host` and a backslash host leave the site). */
const SAME_ORIGIN_PATH = /^\/(?![/\\])/

function sitePath(value: unknown): string | null {
  return typeof value === 'string' && SAME_ORIGIN_PATH.test(value) ? value : null
}

/** An absolute http(s) address (the Edvantage ID pages): never `javascript:`, `data:` or a bare path. */
function httpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? value : null
  } catch {
    return null
  }
}

/** Accepts the raw JSON the config route answers with, and an enveloped `data` member, and ignores anything it cannot read. */
export function parseAuthConfig(body: unknown): AuthConfig {
  const object = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  const inner = object.data && typeof object.data === 'object' ? (object.data as Record<string, unknown>) : object
  // An unknown mode is `legacy`, exactly as the API reads its own flag.
  const mode: AuthMode = inner.mode === 'oidc' || inner.mode === 'both' ? inner.mode : 'legacy'
  return {
    mode,
    // Every address is checked, so a wrong answer from the API cannot send people anywhere but this site or Edvantage ID.
    start_url: sitePath(inner.start_url) ?? LEGACY_CONFIG.start_url,
    signup_url: sitePath(inner.signup_url),
    account_url: httpUrl(inner.account_url) ?? LEGACY_CONFIG.account_url,
    manage_url: httpUrl(inner.manage_url) ?? LEGACY_CONFIG.manage_url,
  }
}

const CONFIG_TIMEOUT_MS = 6_000

/**
 * Ask the API once and remember the answer. A 404 is an API without the route (`legacy`). A network failure, a timeout or a
 * 5xx is "we could not find out": the status becomes `failed` and nothing is remembered, so the next call asks again.
 */
export function loadAuthConfig(options: { force?: boolean } = {}): Promise<AuthConfig | null> {
  if (!options.force) {
    if (snapshot.status === 'ready') return Promise.resolve(snapshot.config)
    if (pending) return pending
  }
  publish({ status: 'loading', config: snapshot.config })
  const request = (async (): Promise<AuthConfig | null> => {
    try {
      const response = await fetch(apiUrl('/auth/config'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(CONFIG_TIMEOUT_MS),
      })
      if (response.status === 404) {
        publish({ status: 'ready', config: LEGACY_CONFIG })
        return LEGACY_CONFIG
      }
      if (!response.ok) throw new Error(`auth config ${response.status}`)
      const config = parseAuthConfig(await response.json())
      publish({ status: 'ready', config })
      return config
    } catch {
      publish({ status: 'failed', config: null })
      return null
    }
  })().finally(() => {
    if (pending === request) pending = null
  })
  pending = request
  return request
}

export function useAuthConfig(): { status: AuthConfigStatus; config: AuthConfig | null; mode: AuthMode } {
  const current = useSyncExternalStore(subscribe, () => snapshot, () => IDLE)
  return { status: current.status, config: current.config, mode: current.config?.mode ?? 'legacy' }
}

export function currentAuthConfig(): AuthConfig | null {
  return snapshot.config
}

/** Tests only: start from a known state. */
export function setAuthConfigForTests(config: AuthConfig | null, status?: AuthConfigStatus) {
  pending = null
  publish(config ? { status: status ?? 'ready', config } : { status: status ?? 'idle', config: null })
}

export type StartOptions = { returnTo?: string; intent?: 'admin' | 'student'; prompt?: 'create' | 'login' }

/**
 * The URL that starts an Edvantage ID sign-in. `return_to` is checked again by the API's `safe_return`, but is only ever
 * sent as a path on this origin here.
 */
export function oidcStartUrl(config: AuthConfig, options: StartOptions = {}): string {
  const [path, existing = ''] = config.start_url.split('?')
  const query = new URLSearchParams(existing)
  if (options.intent) query.set('intent', options.intent)
  if (options.prompt) query.set('prompt', options.prompt)
  if (options.returnTo && options.returnTo.startsWith('/') && !options.returnTo.startsWith('//')) query.set('return_to', options.returnTo)
  const serialised = query.toString()
  return serialised ? `${path}?${serialised}` : path
}

/** The Edvantage ID origin, from the account URL the API publishes. */
export function idOrigin(config: AuthConfig): string {
  try {
    return new URL(config.account_url).origin
  } catch {
    return new URL(LEGACY_CONFIG.account_url).origin
  }
}

/** A page on Edvantage ID: `idUrl(config, '/forgot')`. */
export function idUrl(config: AuthConfig, path: string): string {
  return `${idOrigin(config)}${path}`
}

/**
 * Only follow a sign-out redirect the API produced for Edvantage ID or for this site. Anything else is dropped, so a
 * tampered response can never turn sign-out into an open redirect.
 */
export function trustedRedirect(target: unknown, config: AuthConfig | null): string | null {
  if (typeof target !== 'string' || !target) return null
  try {
    const url = new URL(target, window.location.origin)
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`
    if (config && url.origin === idOrigin(config) && url.protocol === 'https:') return url.toString()
    if (config && url.origin === idOrigin(config) && url.hostname === 'localhost') return url.toString()
  } catch {
    return null
  }
  return null
}

/** The one place the page leaves the app for another origin (a seam the tests replace). */
export const leaveTo = {
  go(url: string) {
    window.location.assign(url)
  },
}
